'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ChevronRight,
  ClipboardList,
  Languages,
  Loader2,
  MapPin,
  Scan,
} from 'lucide-react';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import { NoticeBanner } from '@/shared/ui';
import { getLocaleByCode } from '@/shared/constants';
import { EstimateLanguageSheet } from './EstimateLanguageSheet';
import { EstimateSpecialRequirementsSheet } from './EstimateSpecialRequirementsSheet';
import type { AreaUnit, Estimate } from '../api/estimate-service';

/**
 * Display values for the settings list, decoupled from the `Estimate` API
 * shape so the same component serves two hosts:
 * - a COMPLETED estimate (EstimateEditor / EstimateSettingsPanel) — values
 *   come from the estimate resource, edits PATCH the server;
 * - the pre-generation DRAFT on the working intro screen — values come from
 *   the intro defaults + the in-memory draft store, edits flip the draft only
 *   (estimate-draft.store).
 */
export interface EstimateSettingsValues {
  /** Project's CURRENT region display label (live value), null when unset. */
  regionLabel: string | null;
  /** Project's CURRENT facade area in the display unit, null when unset. */
  facadeArea: number | null;
  facadeAreaUnit: AreaUnit;
  language: string;
  specialRequirements: string | null;
  /**
   * Server-computed divergence flags — completed estimates only (a draft has
   * no generation snapshot to diverge from).
   */
  hasDivergedConstructionRegion?: boolean;
  hasDivergedFacadeArea?: boolean;
}

/** Map a completed estimate to the settings-list display values. */
export function toEstimateSettingsValues(
  estimate: Estimate
): EstimateSettingsValues {
  return {
    regionLabel: estimate.project_region_label,
    facadeArea: estimate.project_facade_area,
    facadeAreaUnit: estimate.facade_area_unit,
    language: estimate.language,
    specialRequirements: estimate.special_requirements,
    hasDivergedConstructionRegion: estimate.has_diverged_construction_region,
    hasDivergedFacadeArea: estimate.has_diverged_facade_area,
  };
}

interface EstimateSettingsListProps {
  values: EstimateSettingsValues;
  disabled?: boolean;
  /**
   * Switch the output language (generation input — texts pick it up on the
   * next regenerate).
   */
  onLanguageChange: (language: string) => void;
  /** A language switch is in flight. */
  isChangingLanguage?: boolean;
  /** Open the project goals editor — where the region/facade area are set. */
  onOpenProjectGoals: () => void;
  /**
   * Persist the special-requirements text (free-text generation prompt;
   * null clears it). Never flips `is_edited`.
   */
  onSpecialRequirementsSave: (value: string | null) => void;
}

/**
 * Estimate settings as a uniform settings-list group (render/album-settings
 * parity): Region → Facade area → Language → Special requirements, each row
 * icon + title + trailing value (+ chevron for drill-downs). Divergence
 * warnings render INLINE under the setting they concern (region warning under
 * the Region row, area warning under the Facade-area row); the staleness
 * banner (`has_newer_renders`) isn't tied to a setting, so the host renders
 * it ABOVE this card.
 *
 * Every row here is a GENERATION INPUT — it affects the next regenerate, not
 * the current document. The display switches (currency / measurement system)
 * deliberately live elsewhere: as tag-toggles on the document title
 * (EstimateTitleTags), because they re-denominate the visible estimate
 * immediately.
 *
 * Region and Facade area are owned by the project goals editor — both rows
 * drill into the goals sheet; the estimate itself never edits them.
 *
 * Used inline at the top of the stacked (single-column) editor, by the
 * two-column inspector aside (EstimateSettingsPanel), and in draft mode by
 * the working intro screen (EstimateDraftSettings) — see
 * `EstimateSettingsValues`.
 */
export function EstimateSettingsList({
  values,
  disabled = false,
  onLanguageChange,
  isChangingLanguage = false,
  onOpenProjectGoals,
  onSpecialRequirementsSave,
}: EstimateSettingsListProps) {
  const t = useTranslations('common.estimate.editor');
  // Album-settings parity: the Language row reuses the album's label.
  const tAlbum = useTranslations('album.settings');

  // Special-requirements drill-down (NestedSheet) — opened by the row below.
  const [showSpecialRequirementsSheet, setShowSpecialRequirementsSheet] =
    useState(false);

  // Output-language drill-down (NestedSheet) — opened by the Language row.
  const [showLanguageSheet, setShowLanguageSheet] = useState(false);

  const languageLabel =
    getLocaleByCode(values.language)?.name ?? values.language;

  const areaUnitLabel =
    values.facadeAreaUnit === 'ft2' ? t('units.ft2') : t('units.m2');

  return (
    <>
      <SettingsCard>
        {/* Region — owned by the project goals; the value is the project's
            CURRENT region (live value), not the snapshot the estimate was
            calculated with — the divergence banner below covers the mismatch.
            "—" keeps the row reachable when no region is set. */}
        <button
          type="button"
          onClick={onOpenProjectGoals}
          disabled={disabled}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <MapPin className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t('region')}
          </span>
          <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[160px]">
            {values.regionLabel || '—'}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>

        {/* Server-computed divergence flag (`has_diverged_construction_region`):
            the construction region was edited in the project goals after
            generation — regional pricing no longer matches, a regenerate is
            needed. Inline under the Region row (no value interpolation — the
            region may have been cleared). */}
        {values.hasDivergedConstructionRegion && (
          <div className="px-4 pb-3">
            <NoticeBanner variant="warning" message={t('regionDivergedWarning')} />
          </div>
        )}

        <div className="border-t border-border/30 mx-5" />

        {/* Facade area — owned by the project goals (same drill-down as
            Region); the value is the project's CURRENT area (live value in the
            display unit), not the snapshot the estimate was calculated with.
            The estimate never edits it and never recalculates on change. "—"
            keeps the row reachable when no area is set yet. */}
        <button
          type="button"
          onClick={onOpenProjectGoals}
          disabled={disabled}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <Scan className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t('facadeArea')}
          </span>
          <span className="text-list-item-subtitle text-muted-foreground tabular-nums">
            {values.facadeArea != null
              ? `${values.facadeArea} ${areaUnitLabel}`
              : '—'}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>

        {/* Server-computed divergence flag (`has_diverged_facade_area`): the
            project's facade area moved away from the snapshot this estimate was
            calculated with — the costs need a regenerate to pick it up. Inline
            under the Facade-area row (no value interpolation — the row above
            already shows the current area). */}
        {values.hasDivergedFacadeArea && (
          <div className="px-4 pb-3">
            <NoticeBanner
              variant="warning"
              message={t('facadeAreaDivergedWarning')}
            />
          </div>
        )}

        <div className="border-t border-border/30 mx-5" />

        {/* Output language — drills into the language NestedSheet (album-
            language parity: same locale list). A generation input: the
            generated texts pick the new language up on the next regenerate. */}
        <button
          type="button"
          onClick={() => setShowLanguageSheet(true)}
          disabled={disabled || isChangingLanguage}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <Languages className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {tAlbum('language')}
          </span>
          {isChangingLanguage && (
            <Loader2
              className="h-4 w-4 animate-spin text-muted-foreground"
              strokeWidth={1.5}
            />
          )}
          <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[160px]">
            {languageLabel}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>

        <div className="border-t border-border/30 mx-5" />

        {/* Special requirements — a free-text generation prompt scoped to the
            estimate (preferred brands, works to include/exclude). Drills into
            its own NestedSheet editor; the trailing value shows the first
            line of the saved text. */}
        <button
          type="button"
          onClick={() => setShowSpecialRequirementsSheet(true)}
          disabled={disabled}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <ClipboardList
            className="h-5 w-5 text-primary shrink-0"
            strokeWidth={1.5}
          />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t('specialRequirements')}
          </span>
          <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[160px]">
            {values.specialRequirements?.split('\n')[0]?.trim() || '—'}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>
      </SettingsCard>

      {/* Output-language drill-down. Out-of-flow (NestedSheet renders
          absolutely / portaled), so it adds no row to the settings group. */}
      <EstimateLanguageSheet
        open={showLanguageSheet}
        onOpenChange={setShowLanguageSheet}
        value={values.language}
        onSelect={onLanguageChange}
      />

      {/* Special-requirements drill-down — same out-of-flow hosting as the
          language sheet, so both layouts get it with zero host changes. */}
      <EstimateSpecialRequirementsSheet
        open={showSpecialRequirementsSheet}
        onOpenChange={setShowSpecialRequirementsSheet}
        value={values.specialRequirements}
        onSave={onSpecialRequirementsSave}
      />
    </>
  );
}
